import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Plus } from "lucide-react";
import { postDividend, postInvestments } from "../utilities/api";
import { cn } from "../utilities/helpers";
import { Button } from "./Button";
import { TransactionFormDialog } from "./transactions/TransactionForm";

const emptyForm = () => ({
	asset: "",
	value: "",
	units: "",
	currency: "USD",
	transactionType: "BUY",
	transactionDate: new Date().toISOString().slice(0, 10),
});

const DASHBOARD_KEYS = [
	"fetchInvestments",
	"fetchInvestmentSummary",
	"fetchAssets",
	"fetchAllAssetLatestPrice",
	"fetchDaily",
	"fetchDividends",
];

const readError = (err, fallback) => {
	const responseError = err?.response?.data?.error;
	const availableUnits = err?.response?.data?.availableUnits;
	if (responseError && typeof availableUnits === "number") {
		return `${responseError}. Available units: ${availableUnits}`;
	}
	return responseError || err?.message || fallback;
};

const AddInvestment = ({ triggerClassName } = {}) => {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const queryClient = useQueryClient();

	const handleSuccess = () => {
		DASHBOARD_KEYS.forEach((key) => queryClient.invalidateQueries(key));
		setOpen(false);
	};

	const mutation = useMutation(postInvestments, {
		onSuccess: handleSuccess,
		onError: (err) => setError(readError(err, "Failed to add transaction")),
	});

	const dividendMutation = useMutation(postDividend, {
		onSuccess: handleSuccess,
		onError: (err) => setError(readError(err, "Failed to add dividend")),
	});

	const handleOpen = () => {
		setError("");
		setOpen(true);
	};

	const handleSubmit = (values) => {
		setError("");
		if (values.transactionType === "DIVIDEND") {
			dividendMutation.mutate({
				asset: values.asset,
				amount: values.value,
				currency: values.currency,
				date: values.transactionDate,
			});
			return;
		}
		mutation.mutate({
			asset: values.asset,
			value: values.value,
			units: values.units,
			currency: values.currency,
			transactionType: values.transactionType,
			transactionDate: values.transactionDate,
		});
	};

	return (
		<>
			<Button
				size="sm"
				className={cn("gap-1.5", triggerClassName)}
				onClick={handleOpen}
				aria-haspopup="dialog"
			>
				<Plus className="h-4 w-4" aria-hidden="true" />
				<span className="hidden sm:inline">Add transaction</span>
				<span className="sm:hidden">Add</span>
			</Button>
			<TransactionFormDialog
				open={open}
				onClose={() => setOpen(false)}
				title="Add transaction"
				description="Record a buy, sell or dividend. Amounts are converted at today's exchange rate."
				initialValues={emptyForm()}
				allowDividend
				submitLabel="Add transaction"
				isSubmitting={mutation.isLoading || dividendMutation.isLoading}
				serverError={error}
				onSubmit={handleSubmit}
			/>
		</>
	);
};

export default AddInvestment;
